import { useParams, useNavigate, Link } from "react-router-dom";
import { Phone, ArrowLeft, ArrowRight, Check, Quote } from "lucide-react";
import NavBar from "@/components/site/NavBar";
import Footer from "@/components/site/Footer";
import StickyCallButton from "@/components/site/StickyCallButton";
import BusinessLeadForm from "@/components/site/BusinessLeadForm";
import { b2bVerticals } from "@/data/b2b";
import useSEO from "@/hooks/useSEO";
import { SITE_URL, BUSINESS_ID } from "@/data/structuredData";
import { trackCallConversion } from "@/lib/tracking";

const BusinessVerticalPage = () => {
  const { vertical: slug } = useParams();
  const navigate = useNavigate();
  const v = b2bVerticals[slug];

  useSEO({
    title: v?.metaTitle,
    description: v?.metaDescription,
    canonical: v ? `${SITE_URL}/for/${v.slug}` : undefined,
    jsonLd: v
      ? {
          "@context": "https://schema.org",
          "@type": "Service",
          name: v.h1,
          description: v.hero,
          provider: { "@id": BUSINESS_ID },
          areaServed: [
            { "@type": "City", name: "Pittsburgh" },
            { "@type": "AdministrativeArea", name: "Allegheny County" },
          ],
          audience: { "@type": "BusinessAudience", audienceType: v.label },
          serviceType: v.label,
          url: `${SITE_URL}/for/${v.slug}`,
        }
      : undefined,
  });

  if (!v) {
    return (
      <div className="min-h-screen bg-[#F4F4F5]">
        <NavBar />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-display uppercase text-4xl mb-4">Page not found</h1>
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 bg-[#D9480F] text-white px-5 py-3 font-display uppercase tracking-tight border-2 border-black"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const Icon = v.Icon;

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      <NavBar />

      {/* Breadcrumb */}
      <div className="border-b border-zinc-900 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-600">
          <Link to="/" className="hover:text-[#D9480F]">Home</Link>
          <span>/</span>
          <Link to="/for/realtors" className="hover:text-[#D9480F]">Business</Link>
          <span>/</span>
          <span className="text-[#D9480F] font-bold">{v.label}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="border-b border-zinc-900 bg-[#F4F4F5]">
        <div className="bg-grid">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7 flex flex-col">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#0B2B40] text-white flex items-center justify-center border-2 border-black">
                  <Icon className="w-6 h-6" strokeWidth={2} />
                </div>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D9480F] font-bold">
                  {v.code} · {v.label}
                </p>
              </div>
              <h1 className="font-display uppercase font-semibold leading-[0.9] tracking-tighter text-5xl md:text-7xl mb-6">
                {v.h1}
              </h1>
              <p className="font-display text-xl md:text-2xl text-[#D9480F] mb-6">
                {v.tagline}
              </p>
              <p className="text-zinc-700 text-base md:text-lg leading-relaxed max-w-2xl mb-8">
                {v.hero}
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="tel:4847643392"
                  onClick={() => trackCallConversion(`b2b_${v.slug}_hero_call`)}
                  className="inline-flex items-center gap-3 bg-[#D9480F] text-white px-6 py-4 font-display uppercase tracking-tight text-base hover:bg-[#E8590C] border-2 border-black hard-shadow-sm"
                >
                  <Phone className="w-5 h-5" strokeWidth={2.5} />
                  Call 484-764-3392
                </a>
                <a
                  href="#request"
                  className="inline-flex items-center gap-3 bg-transparent border-2 border-[#0B2B40] text-[#0B2B40] px-6 py-4 font-display uppercase tracking-tight text-base hover:bg-[#0B2B40] hover:text-white"
                >
                  Request Proposal →
                </a>
              </div>
            </div>

            {/* Painpoints sidebar */}
            <aside className="lg:col-span-5">
              <div className="bg-[#0B2B40] text-white border-2 border-black p-6 hard-shadow-orange">
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#F59E0B] font-bold mb-3">
                  Sound familiar?
                </div>
                <ul className="space-y-3">
                  {v.painpoints.map((p, i) => (
                    <li key={i} className="text-sm md:text-base leading-relaxed flex items-start gap-3">
                      <span className="text-[#D9480F] font-display text-lg leading-none flex-shrink-0 mt-0.5">✕</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="border-b border-zinc-900 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D9480F] font-bold mb-4">
            Why us for {v.label}
          </p>
          <h2 className="font-display uppercase text-3xl md:text-5xl font-semibold tracking-tighter leading-[0.9] mb-12">
            Built for how <br />
            <span className="text-[#0B2B40]">you actually work.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {v.valueProps.map((vp, i) => (
              <article key={i} className="bg-[#F4F4F5] border-2 border-black p-6 flex items-start gap-4">
                <div className="w-10 h-10 bg-[#D9480F] text-white flex items-center justify-center border-2 border-black font-display text-lg flex-shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div>
                  <h3 className="font-display uppercase text-lg md:text-xl tracking-tight mb-2">
                    {vp.title}
                  </h3>
                  <p className="text-zinc-700 text-sm md:text-base leading-relaxed">
                    {vp.body}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="border-b border-zinc-900 bg-[#F59E0B]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20 text-center">
          <Quote className="w-12 h-12 text-black mx-auto mb-6" strokeWidth={2.5} />
          <blockquote className="font-display uppercase text-2xl md:text-4xl font-semibold tracking-tight leading-tight text-black mb-6">
            &ldquo;{v.testimonial.quote}&rdquo;
          </blockquote>
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-black/70 font-bold">
            — {v.testimonial.author}
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="request" className="border-b border-zinc-900 bg-[#0B2B40] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#F59E0B] font-bold mb-4">
              [ Get Started ]
            </p>
            <h2 className="font-display uppercase text-4xl md:text-6xl font-semibold tracking-tighter leading-[0.9] mb-6">
              {v.ctaHeadline}
            </h2>
            <p className="text-zinc-200 leading-relaxed text-base md:text-lg mb-8 max-w-lg">
              {v.ctaSub}
            </p>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#F59E0B] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                <span>PA License #BU15680 · Fully insured</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#F59E0B] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                <span>1 business day response guaranteed</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#F59E0B] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                <span>Se habla español</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="w-5 h-5 text-[#F59E0B] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                <span>Locally owned — Pittsburgh</span>
              </li>
            </ul>
          </div>
          <div className="lg:col-span-7">
            <div className="bg-white text-zinc-900 border-2 border-black p-8 md:p-10 hard-shadow-orange">
              <BusinessLeadForm vertical={v} />
            </div>
          </div>
        </div>
      </section>

      {/* Cross-sell to other verticals */}
      <section className="bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16">
          <h3 className="font-display uppercase text-2xl md:text-3xl tracking-tight mb-6">
            Also for business
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.values(b2bVerticals)
              .filter((o) => o.slug !== v.slug)
              .slice(0, 4)
              .map((o) => {
                const OIcon = o.Icon;
                return (
                  <Link
                    key={o.slug}
                    to={`/for/${o.slug}`}
                    className="bg-white border-2 border-black p-4 flex items-center gap-3 group hover:bg-[#F59E0B]/20 transition-colors"
                  >
                    <div className="w-10 h-10 bg-[#0B2B40] text-white flex items-center justify-center flex-shrink-0">
                      <OIcon className="w-5 h-5" strokeWidth={2} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-display uppercase text-sm tracking-tight truncate group-hover:text-[#D9480F]">
                        {o.label}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-zinc-400 group-hover:text-[#D9480F] flex-shrink-0" strokeWidth={2.5} />
                  </Link>
                );
              })}
          </div>
        </div>
      </section>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default BusinessVerticalPage;
